import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormText,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Nav,
  NavItem,
  NavLink,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Row,
  TabContent,
  TabPane,
  Table,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import classNames from "classnames";
import { ApiError, requestJson } from "../../../helpers/api";
import { obtenerEmpresaActivaId } from "../../../utils/empresa";
import type { MovimientoBaseConfig as MovimientoBaseConfigType } from "./MovimientoBase.types";
import MovimientoFormulario from "./MovimientoFormulario";
import MovimientoListado from "./MovimientoListado";
import {
  ClienteCrearModal,
  ClienteSeleccionModal,
  ChoferCrearModal,
  ChoferSeleccionModal,
  VehiculoCrearModal,
  VehiculoSeleccionModal,
} from "./MovimientoModales";
import {
  MovimientoDetalle,
  MovimientoResumen,
  CatalogoDeposito,
  CatalogoArticulo,
  CatalogoCliente,
  CatalogoChofer,
  CatalogoVehiculo,
  CatalogoSucursal,
  CatalogoPunto,
  CatalogoTalonario,
  MovimientoCabecera,
  CatalogosState,
  ClienteFormState,
  ChoferFormState,
  VehiculoFormState,
} from "./MovimientoBase.types";
import MovimientoPlantillasModal, {
  PlantillaContextForm,
  PlantillaResumen,
} from "./MovimientoPlantillasModal";
import MovimientoPlantillasCrearModal from "./MovimientoPlantillasCrearModal";

export type { MovimientoBaseConfig } from "./MovimientoBase.types";

type PlantillaParametros = {
  depositoId: string;
  sucursalId?: string;
  puntoExpedicionId?: string;
};

const formatearFechaLocal = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatearHoraLocal = (date: Date) => {
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  return `${hours}:${minutes}`;
};

const formatearFechaCortaPY = (isoString: string | null) => {
  if (!isoString) return "-";
  const fecha = new Date(isoString);
  if (Number.isNaN(fecha.getTime())) return "-";
  return fecha.toLocaleDateString("es-PY");
};

const combinarFechaHora = (fecha: string, hora: string) => {
  if (!fecha) return null;
  const [year, month, day] = fecha.split("-").map((value) => Number(value));
  if ([year, month, day].some((value) => Number.isNaN(value))) {
    return null;
  }
  let hour = 0;
  let minute = 0;
  if (hora) {
    const [h, m] = hora.split(":").map((value) => Number(value));
    if (!Number.isNaN(h)) hour = h;
    if (!Number.isNaN(m)) minute = m;
  }
  return new Date(year, month - 1, day, hour, minute);
};

interface MovimientoBaseProps {
  config: MovimientoBaseConfigType;
}

const MovimientoBase: React.FC<MovimientoBaseProps> = ({ config }) => {
  useEffect(() => {
    document.title = config.documentTitle;
  }, [config.documentTitle]);

  const [tabActiva, setTabActiva] = useState<string>("formulario");
  const [busqueda, setBusqueda] = useState("");
  const [filtrosVisibles, setFiltrosVisibles] = useState(false);

  const movimientos = useMemo<MovimientoResumen[]>(
    () => [
      {
        codigo: "MOV-2026-001",
        sucursal: "Casa Central",
        fecha: "2026-01-15",
        hora: "08:30",
        origen: "Depósito Central",
        destino: "Sucursal Mariscal",
        cliente: "Rest. La Esquina",
        estado: "en tránsito",
      },
      {
        codigo: "MOV-2026-002",
        sucursal: "Shopping del Sol",
        fecha: "2026-01-16",
        hora: "11:15",
        origen: "Depósito SD",
        destino: "Depósito Central",
        cliente: "Cliente interno",
        estado: "entregado",
      },
    ],
    [],
  );

  const movimientosFiltrados = useMemo(() => {
    if (!busqueda.trim()) return movimientos;
    return movimientos.filter(
      (mov) =>
        mov.codigo.toLowerCase().includes(busqueda.toLowerCase()) ||
        mov.cliente.toLowerCase().includes(busqueda.toLowerCase()) ||
        mov.sucursal.toLowerCase().includes(busqueda.toLowerCase()) ||
        mov.origen.toLowerCase().includes(busqueda.toLowerCase()) ||
        mov.destino.toLowerCase().includes(busqueda.toLowerCase()),
    );
  }, [busqueda, movimientos]);

  const crearCabeceraInicial = (): MovimientoCabecera => {
    const ahora = new Date();
    return {
      concepto: config.conceptoValor,
      fecha: formatearFechaLocal(ahora),
      hora: formatearHoraLocal(ahora),
      sucursal: "",
      depositoOrigenCabecera: "",
      depositoDestinoCabecera: "",
      numeroDocumento: "",
      pedidoRelacionado: "",
      personaDocumento: "",
      personaNombre: "",
      personaDireccion: "",
      choferId: "",
      choferNombre: "",
      choferDocumento: "",
      vehiculoId: "",
      vehiculoDescripcion: "",
      vehiculoMatricula: "",
      motivo: "",
      referencia: "",
      sucursalTalonario: "",
      puntoExpedicionId: "",
      talonarioId: "",
      numeroDocumentoPreview: "",
    };
  };

  const handlePlantillaContextChange = (campo: keyof typeof plantillaContextoForm, valor: string) => {
    const siguienteForm = {
      ...plantillaContextoForm,
      [campo]: valor,
    };
    if (campo === "sucursalId") {
      siguienteForm.depositoId = "";
      siguienteForm.puntoId = "";
    } else if (campo === "depositoId") {
      siguienteForm.puntoId = "";
    }
    setPlantillaContextoForm(siguienteForm);

    if (campo === "sucursalId") {
      setPlantillaCatalogos((prev) => ({ ...prev, depositos: [], puntos: [] }));
      if (valor) {
        cargarDepositosPlantilla(valor).then((depositos) => {
          if (
            siguienteForm.depositoId &&
            depositos.some((deposito) => String(deposito.id) === siguienteForm.depositoId)
          ) {
            cargarPuntosPlantilla(
              siguienteForm.depositoId,
              siguienteForm.sucursalId || undefined,
            );
          }
        });
      }
      return;
    }

    if (campo === "depositoId") {
      setPlantillaCatalogos((prev) => ({ ...prev, puntos: [] }));
      if (valor) {
        cargarPuntosPlantilla(
          valor,
          siguienteForm.sucursalId || undefined,
        );
      }
    }
  };

  const handlePlantillaCrearDatosChange = (campo: keyof typeof plantillaCrearDatos, valor: string) => {
    setPlantillaCrearDatos((prev) => ({ ...prev, [campo]: valor }));
  };

  const obtenerParametrosPlantillaDesdeFormulario = (form = plantillaContextoForm) => {
    if (config.requiereTalonario) {
      if (!form.sucursalId || !form.depositoId || !form.puntoId) {
        return {
          ok: false,
          mensaje: "Completá la sucursal, el depósito y el punto/caja para continuar.",
        } as const;
      }
      return {
        ok: true,
        params: {
          sucursalId: form.sucursalId,
          depositoId: form.depositoId,
          puntoExpedicionId: form.puntoId,
        },
      } as const;
    }
    if (!form.depositoId) {
      return { ok: false, mensaje: "Seleccioná el depósito para buscar plantillas." } as const;
    }
    return { ok: true, params: { depositoId: form.depositoId } } as const;
  };

  const obtenerContextoDesdeFormulario = (form = plantillaContextoForm) => ({
    sucursal: form.sucursalId
      ? catalogos.sucursales.find((s) => String(s.id) === form.sucursalId)?.fuente ?? null
      : null,
    deposito: form.depositoId
      ? catalogos.depositos.find((d) => String(d.id) === form.depositoId)?.fuente ?? null
      : null,
    punto: form.puntoId
      ? catalogos.puntos.find((p) => String(p.id) === form.puntoId)?.fuente ?? null
      : null,
  });

  const crearDetalleVacio = (): MovimientoDetalle => ({
    id: Date.now(),
    depositoSalida: "",
    depositoEntrada: "",
    codigo: "",
    unidad: "",
    cantidad: "",
    descripcion: "",
    grupo: "",
    orden: undefined,
  });

  const normalizarPlantillaDetalles = (detalles: MovimientoDetalle[]) =>
    detalles.map((detalle, index) => ({
      ...detalle,
      orden: index + 1,
    }));

  const [cabecera, setCabecera] = useState<MovimientoCabecera>(crearCabeceraInicial);

  const [detalles, setDetalles] = useState<MovimientoDetalle[]>([crearDetalleVacio()]);
  const [guardando, setGuardando] = useState(false);
  const [alerta, setAlerta] = useState<{ tipo: "success" | "danger"; mensaje: string } | null>(null);
  const dismissAlerta = () => setAlerta(null);
  const [catalogos, setCatalogos] = useState<CatalogosState>({
    depositos: [],
    articulos: [],
    clientes: [],
    choferes: [],
    vehiculos: [],
    motivos: config.motivoOpciones ?? [],
    sucursales: [],
    puntos: [],
    talonarios: [],
  });
  const [plantillaCatalogos, setPlantillaCatalogos] = useState<{
    depositos: CatalogoDeposito[];
    puntos: CatalogoPunto[];
  }>({
    depositos: [],
    puntos: [],
  });
  const [catalogosCargando, setCatalogosCargando] = useState(false);
  const [catalogosError, setCatalogosError] = useState<string | null>(null);

  const fechaHoraSeleccionada = useMemo(
    () => combinarFechaHora(cabecera.fecha, cabecera.hora) ?? undefined,
    [cabecera.fecha, cabecera.hora],
  );
  const [clienteModalAbierto, setClienteModalAbierto] = useState(false);
  const [choferModalAbierto, setChoferModalAbierto] = useState(false);
  const [clienteBusqueda, setClienteBusqueda] = useState("");
  const [choferBusqueda, setChoferBusqueda] = useState("");
  const [vehiculoModalAbierto, setVehiculoModalAbierto] = useState(false);
  const [vehiculoBusqueda, setVehiculoBusqueda] = useState("");
  const [clienteResultados, setClienteResultados] = useState<CatalogoCliente[]>([]);
  const [choferResultados, setChoferResultados] = useState<CatalogoChofer[]>([]);
  const [vehiculoResultados, setVehiculoResultados] = useState<CatalogoVehiculo[]>([]);
  const [clienteModalCargando, setClienteModalCargando] = useState(false);
  const [choferModalCargando, setChoferModalCargando] = useState(false);
  const [vehiculoModalCargando, setVehiculoModalCargando] = useState(false);
  const [clienteModalError, setClienteModalError] = useState<string | null>(null);
  const [choferModalError, setChoferModalError] = useState<string | null>(null);
  const [vehiculoModalError, setVehiculoModalError] = useState<string | null>(null);
  const [clienteCrearAbierto, setClienteCrearAbierto] = useState(false);
  const [choferCrearAbierto, setChoferCrearAbierto] = useState(false);
  const [vehiculoCrearAbierto, setVehiculoCrearAbierto] = useState(false);
  const [clienteForm, setClienteForm] = useState<ClienteFormState>({
    razonSocial: "",
    documento: "",
    direccion: "",
    telefono: "",
    email: "",
  });
  const [choferForm, setChoferForm] = useState<ChoferFormState>({
    nombre: "",
    documento: "",
    telefono: "",
    email: "",
    vehiculoId: "",
  });
  const [vehiculoForm, setVehiculoForm] = useState<VehiculoFormState>({
    descripcion: "",
    matricula: "",
    marca: "",
    modelo: "",
    color: "",
  });
  const [clienteCreando, setClienteCreando] = useState(false);
  const [choferCreando, setChoferCreando] = useState(false);
  const [vehiculoCreando, setVehiculoCreando] = useState(false);
  const [clienteCrearError, setClienteCrearError] = useState<string | null>(null);
  const [choferCrearError, setChoferCrearError] = useState<string | null>(null);
  const [vehiculoCrearError, setVehiculoCrearError] = useState<string | null>(null);
  const [plantillaListadoAbierto, setPlantillaListadoAbierto] = useState(false);
  const [plantillaCargando, setPlantillaCargando] = useState(false);
  const [plantillaError, setPlantillaError] = useState<string | null>(null);
  const [plantillasDisponibles, setPlantillasDisponibles] = useState<PlantillaResumen[]>([]);
  const [plantillaContexto, setPlantillaContexto] = useState<{
    sucursal?: string | null;
    deposito?: string | null;
    punto?: string | null;
  }>({});
  const [plantillaGuardando, setPlantillaGuardando] = useState(false);
  const [plantillaMensaje, setPlantillaMensaje] = useState<string | null>(null);
  const [plantillaCrearModalAbierto, setPlantillaCrearModalAbierto] = useState(false);
  const [plantillaCrearError, setPlantillaCrearError] = useState<string | null>(null);
  const [plantillaContextoForm, setPlantillaContextoForm] = useState<PlantillaContextForm>({
    sucursalId: "",
    depositoId: "",
    puntoId: "",
  });
  const [plantillaCrearDatos, setPlantillaCrearDatos] = useState({
    nombre: "",
    descripcion: "",
  });
  const [plantillaDetalles, setPlantillaDetalles] = useState<MovimientoDetalle[]>(
    normalizarPlantillaDetalles([crearDetalleVacio()]),
  );
  const [talonarioSeleccionado, setTalonarioSeleccionado] = useState<CatalogoTalonario | null>(null);

  const numeroRemisionVisible = config.requiereTalonario
    ? cabecera.numeroDocumentoPreview ||
      talonarioSeleccionado?.proximoNumeroFormateado ||
      ""
    : cabecera.numeroDocumento || "";
  const timbradoDescripcion =
    config.requiereTalonario && talonarioSeleccionado
      ? {
          establecimiento: talonarioSeleccionado.establecimientoCodigo ?? "",
          punto: talonarioSeleccionado.puntoExpedicionCodigo ?? "",
          vigencia: `Vigencia ${formatearFechaCortaPY(
            talonarioSeleccionado.timbradoVigenciaDesde,
          )} a ${formatearFechaCortaPY(talonarioSeleccionado.timbradoVigenciaHasta)}`,
        }
      : null;
  const empresaId = useMemo(
    () => config.empresaId ?? obtenerEmpresaActivaId() ?? 1,
    [config.empresaId],
  );
  const plantillaTipo = useMemo(
    () =>
      (config.documentoTipo ||
        config.conceptoValor ||
        config.conceptoTitulo ||
        "movimiento"
      ).toLowerCase(),
    [config.conceptoTitulo, config.conceptoValor, config.documentoTipo],
  );
  const plantillaContextoFormulario = obtenerContextoDesdeFormulario();
  const plantillaListadoDescripcion = config.requiereTalonario
    ? `Contexto: ${plantillaContexto.sucursal || "-"} · Depósito ${
        plantillaContexto.deposito || "-"
      } · Punto ${plantillaContexto.punto || "-"}`
    : `Contexto: Depósito ${plantillaContexto.deposito || "-"}`;
  useEffect(() => {
    if (!config.requiereTalonario) {
      setPlantillaCatalogos((prev) => ({ ...prev, depositos: catalogos.depositos }));
    }
  }, [config.requiereTalonario, catalogos.depositos]);
  const plantillaCrearDescripcion = config.requiereTalonario
    ? `Contexto seleccionado: ${plantillaContextoFormulario.sucursal || "-"} · Depósito ${
        plantillaContextoFormulario.deposito || "-"
      } · Punto ${plantillaContextoFormulario.punto || "-"}`
    : `Contexto seleccionado: Depósito ${plantillaContextoFormulario.deposito || "-"}`;

  const parseNumero = (valor: string) => {
    if (!valor) return null;
    const numero = Number(valor);
    return Number.isFinite(numero) ? numero : null;
  };

  const construirPayload = () => {
    if (!cabecera.fecha) {
      throw new Error("Ingresá la fecha del movimiento.");
    }

    const items = detalles
      .map((detalle) => {
        const cantidadNumero = Number(detalle.cantidad);
        const cantidad = Number.isFinite(cantidadNumero) ? cantidadNumero : 0;

        return {
          productoId: detalle.codigo?.trim() || null,
          unidadId: parseNumero(detalle.unidad),
          cantidad,
          descripcion: detalle.descripcion?.trim() || null,
          depositoOrigenId: parseNumero(detalle.depositoSalida),
          depositoDestinoId: parseNumero(detalle.depositoEntrada),
        };
      })
      .filter(
        (item) =>
          (item.cantidad !== 0 && !Number.isNaN(item.cantidad)) ||
          (item.productoId && item.productoId.length > 0) ||
          (item.descripcion && item.descripcion.length > 0),
      );

    if (items.length === 0) {
      throw new Error("Agregá al menos un artículo con cantidad válida.");
    }

    const personaId = parseNumero(cabecera.personaDocumento);
    const vehiculoId = parseNumero(cabecera.vehiculoId);
    const personaContacto = [
      cabecera.personaNombre?.trim(),
      cabecera.personaDocumento?.trim(),
      cabecera.personaDireccion?.trim(),
    ]
      .filter(Boolean)
      .join(" · ");

    const depositoPrincipal = parseNumero(cabecera.sucursal);
    const depositoOrigenCabecera = parseNumero(cabecera.depositoOrigenCabecera);
    const depositoDestinoCabecera = parseNumero(cabecera.depositoDestinoCabecera);
    const sucursalTalonario = parseNumero(cabecera.sucursalTalonario);
    const puntoExpedicionId = parseNumero(cabecera.puntoExpedicionId);
    const talonarioId = parseNumero(cabecera.talonarioId);

    if (config.requiereTalonario) {
      if (!sucursalTalonario) {
        throw new Error("Seleccioná la sucursal para la remisión.");
      }
      if (!puntoExpedicionId) {
        throw new Error("Elegí el punto de expedición (caja) para la remisión.");
      }
      if (!talonarioId) {
        throw new Error("Seleccioná el talonario/timbrado a utilizar.");
      }
    }

    return {
      empresaId,
      fechaMovimiento: cabecera.fecha,
      horaMovimiento: cabecera.hora || "00:00",
      depositoOrigenId:
        depositoOrigenCabecera ?? depositoPrincipal ?? null,
      depositoDestinoId: config.usarCabeceraDepositos
        ? depositoDestinoCabecera ?? null
        : null,
      sucursalOrigenId: config.requiereTalonario
        ? sucursalTalonario ?? null
        : !config.usarCabeceraDepositos
          ? depositoPrincipal ?? null
          : null,
      sucursalDestinoId: config.usarCabeceraDepositos
        ? depositoDestinoCabecera ?? null
        : null,
      pedidoReferencia: cabecera.pedidoRelacionado || null,
      motivoId: parseNumero(cabecera.motivo),
      referencia: cabecera.referencia || null,
      personaId,
      personaContacto: personaContacto || null,
      choferId: parseNumero(cabecera.choferId),
      choferNombre: cabecera.choferNombre || null,
      choferDocumento: cabecera.choferDocumento || null,
      vehiculoId,
      vehiculoDescripcion: cabecera.vehiculoDescripcion || null,
      vehiculoMatricula: cabecera.vehiculoMatricula || null,
      talonarioId: talonarioId ?? undefined,
      puntoExpedicionId: puntoExpedicionId ?? undefined,
      items,
    };
  };

  const resetFormulario = () => {
    setCabecera(crearCabeceraInicial());
    setDetalles([crearDetalleVacio()]);
    setTalonarioSeleccionado(null);
    setCatalogos((prev) => ({ ...prev, puntos: [], talonarios: [] }));
  };

  const handleSeleccionCliente = (id: string) => {
    if (!id) {
      setCabecera((prev) => ({
        ...prev,
        personaDocumento: "",
        personaNombre: "",
        personaDireccion: "",
      }));
      return;
    }
    const cliente = catalogos.clientes.find(
      (c) => String(c.id) === id,
    );
    setCabecera((prev) => ({
      ...prev,
      personaDocumento: id,
      personaNombre: cliente?.razon_social ?? "",
      personaDireccion: cliente?.direccion ?? cliente?.documento ?? "",
    }));
  };

  const handleSeleccionChofer = (id: string) => {
    if (!id) {
      setCabecera((prev) => ({
        ...prev,
        choferId: "",
        choferNombre: "",
        choferDocumento: "",
      }));
      handleSeleccionVehiculo("");
      return;
    }
    const chofer = catalogos.choferes.find(
      (c) => String(c.id) === id,
    );
    setCabecera((prev) => ({
      ...prev,
      choferId: id,
      choferNombre: chofer?.nombre ?? "",
      choferDocumento: chofer?.documento ?? "",
    }));
    handleSeleccionVehiculo(
      chofer?.vehiculoId ? String(chofer.vehiculoId) : "",
    );
  };

  const handleSeleccionVehiculo = (id: string) => {
    if (!id) {
      setCabecera((prev) => ({
        ...prev,
        vehiculoId: "",
        vehiculoDescripcion: "",
        vehiculoMatricula: "",
      }));
      if (choferCrearAbierto) {
        setChoferForm((prev) => ({ ...prev, vehiculoId: "" }));
      }
      return;
    }
    const vehiculo = catalogos.vehiculos.find(
      (c) => String(c.id) === id,
    );
    setCabecera((prev) => ({
      ...prev,
      vehiculoId: id,
      vehiculoDescripcion: vehiculo?.descripcion ?? "",
      vehiculoMatricula: vehiculo?.matricula ?? "",
    }));
    if (choferCrearAbierto) {
      setChoferForm((prev) => ({ ...prev, vehiculoId: id }));
    }
  };

  const cargarDepositosPorSucursal = async (sucursalId: string) => {
    if (!sucursalId) {
      setCatalogos((prev) => ({ ...prev, depositos: [] }));
      return [];
    }
    try {
      const params = new URLSearchParams({
        empresaId: String(empresaId),
        sucursalId,
      });
      const data = await requestJson<CatalogoDeposito[]>(
        `/api/stock/catalogos/depositos?${params.toString()}`,
      );
      setCatalogos((prev) => ({ ...prev, depositos: data }));
      return data;
    } catch (error) {
      setAlerta({
        tipo: "danger",
        mensaje:
          error instanceof ApiError
            ? error.message
            : "No pudimos cargar los depósitos asociados.",
      });
      return [];
    }
  };

  const cargarDepositosPlantilla = async (sucursalId: string) => {
    if (!sucursalId) {
      setPlantillaCatalogos((prev) => ({ ...prev, depositos: [], puntos: [] }));
      return [];
    }
    try {
      const params = new URLSearchParams({
        empresaId: String(empresaId),
        sucursalId,
      });
      const data = await requestJson<CatalogoDeposito[]>(
        `/api/stock/catalogos/depositos?${params.toString()}`,
      );
      setPlantillaCatalogos((prev) => ({ ...prev, depositos: data, puntos: [] }));
      return data;
    } catch (error) {
      setPlantillaError(
        error instanceof ApiError
          ? error.message
          : "No pudimos cargar los depósitos asociados.",
      );
      return [];
    }
  };

  const cargarPuntosPlantilla = async (depositoId: string, sucursalId?: string) => {
    if (!depositoId) {
      setPlantillaCatalogos((prev) => ({ ...prev, puntos: [] }));
      return [];
    }
    try {
      const params = new URLSearchParams({
        empresaId: String(empresaId),
        depositoId,
      });
      if (sucursalId) {
        params.set("sucursalId", sucursalId);
      }
      const data = await requestJson<CatalogoPunto[]>(
        `/api/stock/catalogos/puntos-expedicion?${params.toString()}`,
      );
      setPlantillaCatalogos((prev) => ({ ...prev, puntos: data }));
      return data;
    } catch (error) {
      setPlantillaError(
        error instanceof ApiError
          ? error.message
          : "No pudimos cargar los puntos de expedición.",
      );
      return [];
    }
  };

  const cargarPuntosExpedicion = async (
    sucursalId: string,
    depositoId?: string,
  ) => {
    if (!sucursalId) {
      setCatalogos((prev) => ({ ...prev, puntos: [], talonarios: [] }));
      setTalonarioSeleccionado(null);
      return [];
    }
    try {
      const params = new URLSearchParams({
        empresaId: String(empresaId),
        sucursalId,
      });
      if (depositoId) {
        params.append("depositoId", depositoId);
      }
      const data = await requestJson<CatalogoPunto[]>(
        `/api/stock/catalogos/puntos-expedicion?${params.toString()}`,
      );
      setCatalogos((prev) => ({ ...prev, puntos: data, talonarios: [] }));
      setTalonarioSeleccionado(null);
      return data;
    } catch (error) {
      setAlerta({
        tipo: "danger",
        mensaje:
          error instanceof ApiError
            ? error.message
            : "No pudimos cargar los puntos de expedición.",
      });
      return [];
    }
  };

  const cargarTalonarios = async (puntoId: string) => {
    if (!puntoId) {
      setCatalogos((prev) => ({ ...prev, talonarios: [] }));
      setTalonarioSeleccionado(null);
      return [];
    }
    try {
      const params = new URLSearchParams({
        empresaId: String(empresaId),
        tipoDocumento: config.documentoTipo ?? "remision",
        puntoId,
      });
      const data = await requestJson<CatalogoTalonario[]>(
        `/api/stock/catalogos/talonarios?${params.toString()}`,
      );
      setCatalogos((prev) => ({ ...prev, talonarios: data }));
      return data;
    } catch (error) {
      setAlerta({
        tipo: "danger",
        mensaje:
          error instanceof ApiError
            ? error.message
            : "No pudimos cargar el talonario vinculado a la caja.",
      });
      return [];
    }
  };

  const manejarCambioSucursalTalonario = async (valor: string) => {
    setCabecera((prev) => ({
      ...prev,
      sucursalTalonario: valor,
      sucursal: "",
      depositoOrigenCabecera: "",
      puntoExpedicionId: "",
      talonarioId: "",
      numeroDocumentoPreview: "",
    }));
    setTalonarioSeleccionado(null);
    setCatalogos((prev) => ({ ...prev, puntos: [], talonarios: [] }));
    if (config.requiereTalonario) {
      await cargarDepositosPorSucursal(valor);
    }
  };

  const manejarCambioDeposito = async (valor: string) => {
    if (!config.requiereTalonario) {
      handleCabeceraChange("sucursal", valor);
      return;
    }
    setCabecera((prev) => ({
      ...prev,
      sucursal: valor,
      depositoOrigenCabecera: valor,
      puntoExpedicionId: "",
      talonarioId: "",
      numeroDocumentoPreview: "",
    }));
    setTalonarioSeleccionado(null);
    setCatalogos((prev) => ({ ...prev, talonarios: [] }));
    if (valor) {
      const puntos = await cargarPuntosExpedicion(cabecera.sucursalTalonario, valor);
      if (puntos.length === 1) {
        await manejarCambioPuntoExpedicion(String(puntos[0].id));
      }
    } else {
      setCatalogos((prev) => ({ ...prev, puntos: [] }));
    }
  };

  const manejarCambioPuntoExpedicion = async (valor: string) => {
    setCabecera((prev) => ({
      ...prev,
      puntoExpedicionId: valor,
      talonarioId: "",
      numeroDocumentoPreview: "",
    }));
    setTalonarioSeleccionado(null);
    if (!valor) {
      setCatalogos((prev) => ({ ...prev, talonarios: [] }));
      return;
    }
    const talonarios = await cargarTalonarios(valor);
    const talonarioActivo = talonarios[0] ?? null;

    setCabecera((prev) => ({
      ...prev,
      talonarioId: talonarioActivo ? String(talonarioActivo.id) : "",
      numeroDocumentoPreview: talonarioActivo?.proximoNumeroFormateado ?? "",
    }));
    setTalonarioSeleccionado(talonarioActivo);
  };

  const motivosDisponibles =
    catalogos.motivos.length > 0
      ? catalogos.motivos
      : config.motivoOpciones ?? [];

  const abrirClienteModal = () => {
    setClienteResultados(catalogos.clientes);
    setClienteModalError(null);
    setClienteBusqueda("");
    setClienteModalAbierto(true);
  };

  const abrirChoferModal = () => {
    setChoferResultados(catalogos.choferes);
    setChoferModalError(null);
    setChoferBusqueda("");
    setChoferModalAbierto(true);
  };

  const abrirVehiculoModal = () => {
    setVehiculoResultados(catalogos.vehiculos);
    setVehiculoModalError(null);
    setVehiculoBusqueda("");
    setVehiculoModalAbierto(true);
  };

  const buscarClientes = async (termino: string) => {
    setClienteModalCargando(true);
    setClienteModalError(null);
    try {
      const qs = new URLSearchParams({
        empresaId: String(empresaId),
        search: termino,
      }).toString();
      const data = await requestJson<CatalogoCliente[]>(`/api/stock/catalogos/clientes?${qs}`);
      setClienteResultados(data);
    } catch (error) {
      setClienteModalError(
        error instanceof ApiError ? error.message : "No se pudieron cargar los clientes.",
      );
    } finally {
      setClienteModalCargando(false);
    }
  };

  const buscarChoferes = async (termino: string) => {
    setChoferModalCargando(true);
    setChoferModalError(null);
    try {
      const qs = new URLSearchParams({
        empresaId: String(empresaId),
        search: termino,
      }).toString();
      const data = await requestJson<CatalogoChofer[]>(`/api/stock/catalogos/choferes?${qs}`);
      setChoferResultados(data);
    } catch (error) {
      setChoferModalError(
        error instanceof ApiError ? error.message : "No se pudieron cargar los choferes.",
      );
    } finally {
      setChoferModalCargando(false);
    }
  };

  const buscarVehiculos = async (termino: string) => {
    setVehiculoModalCargando(true);
    setVehiculoModalError(null);
    try {
      const qs = new URLSearchParams({
        empresaId: String(empresaId),
        search: termino,
      }).toString();
      const data = await requestJson<CatalogoVehiculo[]>(`/api/stock/catalogos/vehiculos?${qs}`);
      setVehiculoResultados(data);
    } catch (error) {
      setVehiculoModalError(
        error instanceof ApiError ? error.message : "No se pudieron cargar los vehículos.",
      );
    } finally {
      setVehiculoModalCargando(false);
    }
  };

  const crearClienteRapido = async () => {
    if (!clienteForm.razonSocial.trim() || !clienteForm.documento.trim()) {
      setClienteCrearError("Ingresá el nombre y el documento.");
      return;
    }
    setClienteCreando(true);
    setClienteCrearError(null);
    try {
      const payload = {
        empresaId,
        razonSocial: clienteForm.razonSocial.trim(),
        documento: clienteForm.documento.trim(),
        direccion: clienteForm.direccion.trim() || undefined,
        telefono: clienteForm.telefono.trim() || undefined,
        email: clienteForm.email.trim() || undefined,
      };
      const nuevo = await requestJson<{
        id: number;
        razon_social: string;
        documento: string | null;
        direccion: string | null;
      }>(
        "/api/stock/catalogos/clientes",
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      );
      const clienteObj: CatalogoCliente = {
        id: nuevo.id,
        razon_social: nuevo.razon_social,
        documento: nuevo.documento,
        direccion: nuevo.direccion ?? null,
      };
      setCatalogos((prev) => ({
        ...prev,
        clientes: [...prev.clientes, clienteObj],
      }));
      handleSeleccionCliente(String(clienteObj.id));
      setClienteCrearAbierto(false);
      setClienteModalAbierto(false);
      setClienteForm({ razonSocial: "", documento: "", direccion: "", telefono: "", email: "" });
    } catch (error) {
      setClienteCrearError(
        error instanceof ApiError ? error.message : "No se pudo crear el cliente.",
      );
    } finally {
      setClienteCreando(false);
    }
  };

  const crearChoferRapido = async () => {
    if (!choferForm.nombre.trim()) {
      setChoferCrearError("Ingresá el nombre del chofer.");
      return;
    }
    setChoferCreando(true);
    setChoferCrearError(null);
    try {
      const payload = {
        empresaId,
        nombre: choferForm.nombre.trim(),
        documento: choferForm.documento.trim() || undefined,
        telefono: choferForm.telefono.trim() || undefined,
        email: choferForm.email.trim() || undefined,
        vehiculoId: parseNumero(choferForm.vehiculoId) ?? undefined,
      };
      const nuevo = await requestJson<CatalogoChofer>(
        "/api/stock/catalogos/choferes",
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      );
      const choferObj: CatalogoChofer = {
        id: nuevo.id,
        nombre: nuevo.nombre,
        documento: nuevo.documento,
        vehiculoId: nuevo.vehiculoId ?? null,
        vehiculoDescripcion: nuevo.vehiculoDescripcion ?? null,
        vehiculoMatricula: nuevo.vehiculoMatricula ?? null,
      };
      setCatalogos((prev) => ({
        ...prev,
        choferes: [...prev.choferes, choferObj],
      }));
      handleSeleccionChofer(String(choferObj.id));
      setChoferCrearAbierto(false);
      setChoferModalAbierto(false);
      setChoferForm({ nombre: "", documento: "", telefono: "", email: "", vehiculoId: "" });
    } catch (error) {
      setChoferCrearError(
        error instanceof ApiError ? error.message : "No se pudo crear el chofer.",
      );
    } finally {
      setChoferCreando(false);
    }
  };

  const crearVehiculoRapido = async () => {
    if (!vehiculoForm.descripcion.trim() || !vehiculoForm.matricula.trim()) {
      setVehiculoCrearError("Ingresá descripción y matrícula.");
      return;
    }
    setVehiculoCreando(true);
    setVehiculoCrearError(null);
    try {
      const payload = {
        empresaId,
        descripcion: vehiculoForm.descripcion.trim(),
        matricula: vehiculoForm.matricula.trim(),
        marca: vehiculoForm.marca.trim() || undefined,
        modelo: vehiculoForm.modelo.trim() || undefined,
        color: vehiculoForm.color.trim() || undefined,
      };
      const nuevo = await requestJson<{ id: number; descripcion: string; matricula: string | null }>(
        "/api/stock/catalogos/vehiculos",
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      );
      const vehiculoObj: CatalogoVehiculo = {
        id: nuevo.id,
        descripcion: nuevo.descripcion,
        matricula: nuevo.matricula,
      };
      setCatalogos((prev) => ({
        ...prev,
        vehiculos: [...prev.vehiculos, vehiculoObj],
      }));
      handleSeleccionVehiculo(String(vehiculoObj.id));
      setVehiculoCrearAbierto(false);
      setVehiculoModalAbierto(false);
      setVehiculoForm({ descripcion: "", matricula: "", marca: "", modelo: "", color: "" });
    } catch (error) {
      setVehiculoCrearError(
        error instanceof ApiError ? error.message : "No se pudo crear el vehículo.",
      );
    } finally {
      setVehiculoCreando(false);
    }
  };

  useEffect(() => {
    let activo = true;
    const cargarCatalogos = async () => {
      setCatalogosCargando(true);
      setCatalogosError(null);
      try {
        const baseUrl = `/api/stock/catalogos`;
        const qs = `empresaId=${empresaId}`;
        const [
          depositosData,
          articulosData,
          clientesData,
          choferesData,
          vehiculosData,
          motivosData,
          sucursalesData,
        ] = await Promise.all([
          requestJson<CatalogoDeposito[]>(`${baseUrl}/depositos?${qs}`),
          requestJson<CatalogoArticulo[]>(`${baseUrl}/articulos?${qs}`),
          config.mostrarSeccionPersona === false
            ? Promise.resolve([])
            : requestJson<CatalogoCliente[]>(`${baseUrl}/clientes?${qs}`),
          requestJson<CatalogoChofer[]>(`${baseUrl}/choferes?${qs}`),
          requestJson<CatalogoVehiculo[]>(`${baseUrl}/vehiculos?${qs}`),
          config.motivoTipo
            ? requestJson<{ id: number; codigo: string; descripcion: string }[]>(
                `${baseUrl}/motivos?tipo=${config.motivoTipo}`,
              )
            : Promise.resolve([]),
          config.requiereTalonario
            ? requestJson<CatalogoSucursal[]>(`${baseUrl}/sucursales?${qs}`)
            : Promise.resolve([]),
        ]);
        if (!activo) return;
        setCatalogos({
          depositos: config.requiereTalonario ? [] : depositosData,
          articulos: articulosData,
          clientes: clientesData,
          choferes: choferesData,
          vehiculos: vehiculosData,
          motivos:
            motivosData.length > 0
              ? motivosData.map((motivo) => ({
                  value: motivo.codigo,
                  label: motivo.descripcion,
                }))
              : config.motivoOpciones ?? [],
          sucursales: sucursalesData,
          puntos: [],
          talonarios: [],
        });
      } catch (error) {
        if (!activo) return;
        const mensaje =
          error instanceof ApiError
            ? error.message
            : "No se pudieron cargar los catálogos.";
        setCatalogosError(mensaje);
        setAlerta({ tipo: "danger", mensaje });
      } finally {
        if (activo) {
          setCatalogosCargando(false);
        }
      }
    };
    cargarCatalogos();
    return () => {
      activo = false;
    };
  }, [empresaId, config.mostrarSeccionPersona, config.motivoTipo, config.motivoOpciones, config.requiereTalonario]);

  const handleCabeceraChange = (campo: keyof typeof cabecera, valor: string) => {
    setCabecera((prev) => ({ ...prev, [campo]: valor }));
  };

  const handleDetalleChange = (id: number, campo: keyof MovimientoDetalle, valor: string) => {
    setDetalles((prev) => prev.map((detalle) => (detalle.id === id ? { ...detalle, [campo]: valor } : detalle)));
  };

  const agregarLineaDetalle = () => {
    setDetalles((prev) => [...prev, crearDetalleVacio()]);
  };

  const eliminarLineaDetalle = (id: number) => {
    setDetalles((prev) => (prev.length === 1 ? prev : prev.filter((detalle) => detalle.id !== id)));
  };

  const handlePlantillaDetalleChange = (id: number, campo: keyof MovimientoDetalle, valor: string) => {
    setPlantillaDetalles((prev) =>
      normalizarPlantillaDetalles(
        prev.map((detalle) => (detalle.id === id ? { ...detalle, [campo]: valor } : detalle)),
      ),
    );
  };

  const agregarLineaPlantillaDetalle = () => {
    setPlantillaDetalles((prev) =>
      normalizarPlantillaDetalles([...prev, crearDetalleVacio()]),
    );
  };

  const eliminarLineaPlantillaDetalle = (id: number) => {
    setPlantillaDetalles((prev) => {
      const siguientes = prev.length === 1 ? [crearDetalleVacio()] : prev.filter((detalle) => detalle.id !== id);
      return normalizarPlantillaDetalles(siguientes);
    });
  };

  const moverLineaPlantillaDetalle = (id: number, direccion: "up" | "down") => {
    setPlantillaDetalles((prev) => {
      const index = prev.findIndex((detalle) => detalle.id === id);
      if (index === -1) return prev;
      const target = direccion === "up" ? index - 1 : index + 1;
      if (target < 0 || target >= prev.length) {
        return prev;
      }
      const reordenados = [...prev];
      [reordenados[index], reordenados[target]] = [reordenados[target], reordenados[index]];
      return normalizarPlantillaDetalles(reordenados);
    });
  };

  const handleFechaHoraChange = (date: Date | null) => {
    if (date) {
      handleCabeceraChange("fecha", formatearFechaLocal(date));
      handleCabeceraChange("hora", formatearHoraLocal(date));
    } else {
      handleCabeceraChange("fecha", "");
      handleCabeceraChange("hora", "");
    }
  };

  const handleClienteFormChange = (campo: keyof ClienteFormState, valor: string) => {
    setClienteForm((prev) => ({ ...prev, [campo]: valor }));
  };

  const handleChoferFormChange = (campo: keyof ChoferFormState, valor: string) => {
    setChoferForm((prev) => ({ ...prev, [campo]: valor }));
  };

  const handleVehiculoFormChange = (campo: keyof VehiculoFormState, valor: string) => {
    setVehiculoForm((prev) => ({ ...prev, [campo]: valor }));
  };

  const obtenerParametrosPlantilla = ():
    | { ok: true; params: PlantillaParametros }
    | { ok: false; mensaje: string } => {
    if (config.requiereTalonario) {
      if (!cabecera.sucursalTalonario || !cabecera.sucursal || !cabecera.puntoExpedicionId) {
        return {
          ok: false,
          mensaje: "Completá la sucursal, el depósito y el punto/caja para usar una plantilla.",
        };
      }
      return {
        ok: true,
        params: {
          sucursalId: cabecera.sucursalTalonario,
          depositoId: cabecera.sucursal,
          puntoExpedicionId: cabecera.puntoExpedicionId,
        },
      };
    }
    if (!cabecera.sucursal) {
      return { ok: false, mensaje: "Seleccioná el depósito para usar una plantilla de artículos." };
    }
    return { ok: true, params: { depositoId: cabecera.sucursal } };
  };

  const listarPlantillas = async (params: PlantillaParametros) => {
    setPlantillaCargando(true);
    setPlantillaError(null);
    setPlantillaMensaje(null);
    try {
      const qs = new URLSearchParams({ empresaId: String(empresaId) });
      if (params.sucursalId) qs.set("sucursalId", params.sucursalId);
      if (params.depositoId) qs.set("depositoId", params.depositoId);
      if (params.puntoExpedicionId) qs.set("puntoExpedicionId", params.puntoExpedicionId);
      const endpointBase = config.apiPath.replace(/\/$/, "");
      const data = await requestJson<PlantillaResumen[]>(
        `${endpointBase}/plantillas?${qs.toString()}`,
      );
      setPlantillasDisponibles(data);
    } catch (error) {
      setPlantillaError(
        error instanceof ApiError
          ? error.message
          : "No se pudo obtener la plantilla para esta combinación.",
      );
      setPlantillasDisponibles([]);
    } finally {
      setPlantillaCargando(false);
    }
  };

  const abrirPlantillaModal = () => {
    setPlantillaMensaje(null);
    setPlantillaError(null);
    setPlantillaListadoAbierto(true);
    setPlantillasDisponibles([]);
    const formularioInicial: PlantillaContextForm = {
      sucursalId: config.requiereTalonario ? cabecera.sucursalTalonario ?? "" : "",
      depositoId: cabecera.sucursal ?? "",
      puntoId: config.requiereTalonario ? cabecera.puntoExpedicionId ?? "" : "",
    };
    setPlantillaContextoForm(formularioInicial);
    setPlantillaCrearDatos({ nombre: "", descripcion: "" });
    setPlantillaDetalles(normalizarPlantillaDetalles([crearDetalleVacio()]));
    setPlantillaCrearError(null);
    setPlantillaCatalogos({
      depositos: config.requiereTalonario ? [] : catalogos.depositos,
      puntos: [],
    });
    if (config.requiereTalonario && formularioInicial.sucursalId) {
      cargarDepositosPlantilla(formularioInicial.sucursalId).then((depositos) => {
        if (
          formularioInicial.depositoId &&
          depositos.some((deposito) => String(deposito.id) === formularioInicial.depositoId)
        ) {
          cargarPuntosPlantilla(
            formularioInicial.depositoId,
            formularioInicial.sucursalId || undefined,
          );
        }
      });
    }
    buscarPlantillas(formularioInicial, { mostrarError: false });
  };

  const buscarPlantillas = (
    formulario?: PlantillaContextForm,
    options?: { mostrarError?: boolean },
  ) => {
    const mostrarError = options?.mostrarError ?? true;
    const form = formulario ?? plantillaContextoForm;
    const validacion = obtenerParametrosPlantillaDesdeFormulario(form);
    if (!validacion.ok) {
      if (mostrarError) {
        setPlantillaError(validacion.mensaje);
      } else {
        setPlantillaError(null);
      }
      setPlantillaContexto(obtenerContextoDesdeFormulario(form));
      setPlantillasDisponibles([]);
      return;
    }
    setPlantillaError(null);
    setPlantillaMensaje(null);
    setPlantillaContexto(obtenerContextoDesdeFormulario(form));
    void listarPlantillas(validacion.params);
  };

  const manejarActualizarPlantillas = (options?: { mostrarError?: boolean }) => {
    buscarPlantillas(undefined, options);
  };


  const aplicarPlantilla = (plantilla: PlantillaResumen) => {
    if (!plantilla.items || plantilla.items.length === 0) {
      setPlantillaError("Esta plantilla no tiene artículos cargados.");
      return;
    }
    const timestamp = Date.now();
    const detallesDesdePlantilla = plantilla.items.map((item, index) => ({
      id: timestamp + index,
      depositoSalida: item.depositoSalidaId ? String(item.depositoSalidaId) : "",
      depositoEntrada: item.depositoDestinoId ? String(item.depositoDestinoId) : "",
      codigo: item.productoCodigo ?? "",
      unidad:
        item.unidadDescripcion ??
        (item.unidadId ? String(item.unidadId) : ""),
      cantidad: "",
      descripcion: item.productoDescripcion ?? "",
      grupo: item.grupo ?? "",
      orden: item.orden ?? index + 1,
    }));
    if (detallesDesdePlantilla.length > 0) {
      setDetalles(detallesDesdePlantilla);
      setAlerta({
        tipo: "success",
        mensaje: `Se cargó la plantilla con ${detallesDesdePlantilla.length} artículo(s). Recordá completar las cantidades antes de guardar.`,
      });
    }
    setPlantillaListadoAbierto(false);
  };

  const cerrarPlantillaListado = () => {
    setPlantillaListadoAbierto(false);
    setPlantillaCrearModalAbierto(false);
    setPlantillasDisponibles([]);
    setPlantillaError(null);
    setPlantillaMensaje(null);
    setPlantillaGuardando(false);
    setPlantillaCrearError(null);
    setPlantillaCrearDatos({ nombre: "", descripcion: "" });
    setPlantillaDetalles(normalizarPlantillaDetalles([crearDetalleVacio()]));
    setPlantillaCatalogos({ depositos: [], puntos: [] });
  };

  const cerrarPlantillaCrear = () => {
    setPlantillaCrearModalAbierto(false);
    setPlantillaCrearError(null);
    setPlantillaCrearDatos({ nombre: "", descripcion: "" });
    setPlantillaDetalles(normalizarPlantillaDetalles([crearDetalleVacio()]));
  };

  const abrirCrearPlantilla = () => {
    setPlantillaMensaje(null);
    setPlantillaError(null);
    setPlantillaCrearError(null);
    setPlantillaCrearDatos({ nombre: "", descripcion: "" });
    setPlantillaDetalles(normalizarPlantillaDetalles([crearDetalleVacio()]));
    if (config.requiereTalonario && plantillaContextoForm.sucursalId) {
      cargarDepositosPlantilla(plantillaContextoForm.sucursalId).then((depositos) => {
        if (
          plantillaContextoForm.depositoId &&
          depositos.some((deposito) => String(deposito.id) === plantillaContextoForm.depositoId)
        ) {
          cargarPuntosPlantilla(
            plantillaContextoForm.depositoId,
            plantillaContextoForm.sucursalId || undefined,
          );
        }
      });
    }
    setPlantillaCrearModalAbierto(true);
  };

  const guardarPlantilla = async () => {
    const validacion = obtenerParametrosPlantillaDesdeFormulario();
    if (!validacion.ok) {
      setPlantillaCrearError(validacion.mensaje);
      return;
    }
    const depositoId = parseNumero(plantillaContextoForm.depositoId);
    const sucursalId = parseNumero(plantillaContextoForm.sucursalId);
    const puntoId = parseNumero(plantillaContextoForm.puntoId);

    const items = plantillaDetalles
      .map((detalle, index) => {
        const codigo = detalle.codigo?.trim() || null;
        const descripcion = detalle.descripcion?.trim() || null;
        if (!codigo && !descripcion) return null;
        const depositoSalidaId = parseNumero(detalle.depositoSalida);
        const depositoEntradaId = parseNumero(detalle.depositoEntrada);
        const unidadId = parseNumero(detalle.unidad);
        const unidadDescripcion = detalle.unidad?.trim() || null;
        const grupo = detalle.grupo?.trim() || null;
        const orden = detalle.orden ?? index + 1;
        return {
          productoCodigo: codigo,
          productoDescripcion: descripcion,
          descripcion,
          unidadId,
          unidadDescripcion,
          depositoSalidaId,
          depositoDestinoId: depositoEntradaId,
          grupo,
          orden,
        };
      })
      .filter((item): item is NonNullable<typeof item> => Boolean(item));

    if (items.length === 0) {
      setPlantillaCrearError("Agregá al menos un artículo con descripción o código para guardar la plantilla.");
      return;
    }

    if (!depositoId) {
      setPlantillaCrearError("Seleccioná el depósito asociado antes de guardar la plantilla.");
      return;
    }

    setPlantillaGuardando(true);
    setPlantillaCrearError(null);
    setPlantillaMensaje(null);
    try {
      const endpointBase = config.apiPath.replace(/\/$/, "");
      const data = await requestJson<PlantillaResumen[]>(`${endpointBase}/plantillas`, {
        method: "POST",
        body: JSON.stringify({
          empresaId,
          tipo: plantillaTipo,
          sucursalId: config.requiereTalonario ? sucursalId : undefined,
          depositoId,
          puntoExpedicionId: config.requiereTalonario ? puntoId : undefined,
          nombre: plantillaCrearDatos.nombre?.trim() || undefined,
          descripcion: plantillaCrearDatos.descripcion?.trim() || undefined,
          items,
        }),
      });
      setPlantillaMensaje("Plantilla guardada correctamente.");
      setPlantillaContexto(obtenerContextoDesdeFormulario());
      setPlantillasDisponibles(data);
      setPlantillaCrearModalAbierto(false);
      setPlantillaDetalles(normalizarPlantillaDetalles([crearDetalleVacio()]));
    } catch (error) {
      setPlantillaCrearError(
        error instanceof ApiError ? error.message : "No se pudo guardar la plantilla. Intentá nuevamente.",
      );
    } finally {
      setPlantillaGuardando(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setAlerta(null);

    let payload;
    try {
      payload = construirPayload();
    } catch (error) {
      setAlerta({
        tipo: "danger",
        mensaje: error instanceof Error ? error.message : "Completá los datos obligatorios.",
      });
      return;
    }

    setGuardando(true);
    try {
      const respuesta = await requestJson<{ numeroDocumentoFormateado?: string; numeroDocumento?: string }>(
        config.apiPath,
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      );
      const numeroConfirmado =
        respuesta?.numeroDocumentoFormateado ??
        respuesta?.numeroDocumento ??
        cabecera.numeroDocumentoPreview;
      setAlerta({
        tipo: "success",
        mensaje: numeroConfirmado
          ? `${config.conceptoTitulo} registrado (Nro. ${numeroConfirmado}).`
          : `${config.conceptoTitulo} registrado correctamente.`,
      });
      resetFormulario();
      setTabActiva("listado");
    } catch (error) {
      const mensaje =
        error instanceof ApiError
          ? error.message
          : "No se pudo registrar el movimiento. Intentá nuevamente.";
      setAlerta({ tipo: "danger", mensaje });
    } finally {
      setGuardando(false);
    }
  };

    return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={config.breadcrumbTitle}
          pageTitle={config.breadcrumbSection}
          pageLink={config.breadcrumbSectionLink}
          description={config.breadcrumbDescription}
        />
        <Row className="g-3 mb-3">
          <Col xs={12}>
            <Nav tabs className="nav nav-tabs nav-border-top nav-border-top-primary">
              {[
                {
                  id: "formulario",
                  icono: "ri-edit-line",
                  label: `Registrar ${config.conceptoTitulo.toLowerCase()}`,
                },
                {
                  id: "listado",
                  icono: "ri-file-list-3-line",
                  label: `Listado de ${config.conceptoTitulo.toLowerCase()}`,
                },
              ].map((tab) => (
                <NavItem key={tab.id}>
                  <NavLink
                    href="#"
                    className={classNames("px-3 py-2", { active: tabActiva === tab.id })}
                    onClick={(e) => {
                      e.preventDefault();
                      setTabActiva(tab.id);
                    }}
                  >
                    <i className={`${tab.icono} align-middle me-2`} />
                    {tab.label}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
          </Col>
        </Row>
        <Card className="mb-3">
          <CardBody className="pb-0">
            <TabContent activeTab={tabActiva} className="text-muted">
              <TabPane tabId="formulario">
                <MovimientoFormulario
                  config={config}
                  cabecera={cabecera}
                  detalles={detalles}
                  catalogos={catalogos}
                  catalogosCargando={catalogosCargando}
                  catalogosError={catalogosError}
                  motivosDisponibles={motivosDisponibles}
                  guardando={guardando}
                  alerta={alerta}
                  numeroRemisionVisible={numeroRemisionVisible}
                  timbradoDescripcion={timbradoDescripcion}
                  fechaHoraSeleccionada={fechaHoraSeleccionada}
                  onSubmit={handleSubmit}
                  onReset={resetFormulario}
                  onDismissAlerta={dismissAlerta}
                  onCabeceraChange={handleCabeceraChange}
                  onSucursalTalonarioChange={manejarCambioSucursalTalonario}
                  onDepositoChange={manejarCambioDeposito}
                  onPuntoExpedicionChange={manejarCambioPuntoExpedicion}
                  onDetalleChange={handleDetalleChange}
                  onDetalleRemove={eliminarLineaDetalle}
                  onDetalleAdd={agregarLineaDetalle}
                  onAbrirClienteModal={abrirClienteModal}
                  onAbrirChoferModal={abrirChoferModal}
                  onAbrirVehiculoModal={abrirVehiculoModal}
                  onFechaHoraChange={handleFechaHoraChange}
                  onPlantillaClick={abrirPlantillaModal}
                />
                <ClienteSeleccionModal
                  abierto={clienteModalAbierto}
                  busqueda={clienteBusqueda}
                  cargando={clienteModalCargando}
                  error={clienteModalError}
                  resultados={clienteResultados}
                  onClose={() => setClienteModalAbierto(false)}
                  onBusquedaChange={setClienteBusqueda}
                  onBuscar={() => buscarClientes(clienteBusqueda)}
                  onCrear={() => setClienteCrearAbierto(true)}
                  onSeleccion={(id) => {
                    handleSeleccionCliente(id);
                    setClienteModalAbierto(false);
                  }}
                />
                <ChoferSeleccionModal
                  abierto={choferModalAbierto}
                  busqueda={choferBusqueda}
                  cargando={choferModalCargando}
                  error={choferModalError}
                  resultados={choferResultados}
                  onClose={() => setChoferModalAbierto(false)}
                  onBusquedaChange={setChoferBusqueda}
                  onBuscar={() => buscarChoferes(choferBusqueda)}
                  onCrear={() => setChoferCrearAbierto(true)}
                  onSeleccion={(id) => {
                    handleSeleccionChofer(id);
                    setChoferModalAbierto(false);
                  }}
                />
                <VehiculoSeleccionModal
                  abierto={vehiculoModalAbierto}
                  busqueda={vehiculoBusqueda}
                  cargando={vehiculoModalCargando}
                  error={vehiculoModalError}
                  resultados={vehiculoResultados}
                  onClose={() => setVehiculoModalAbierto(false)}
                  onBusquedaChange={setVehiculoBusqueda}
                  onBuscar={() => buscarVehiculos(vehiculoBusqueda)}
                  onCrear={() => setVehiculoCrearAbierto(true)}
                  onSeleccion={(id) => {
                    handleSeleccionVehiculo(id);
                    setVehiculoModalAbierto(false);
                  }}
                />
                <ClienteCrearModal
                  abierto={clienteCrearAbierto}
                  error={clienteCrearError}
                  form={clienteForm}
                  creando={clienteCreando}
                  onClose={() => setClienteCrearAbierto(false)}
                  onChange={handleClienteFormChange}
                  onSubmit={crearClienteRapido}
                />
                <ChoferCrearModal
                  abierto={choferCrearAbierto}
                  error={choferCrearError}
                  form={choferForm}
                  creando={choferCreando}
                  onClose={() => setChoferCrearAbierto(false)}
                  onChange={handleChoferFormChange}
                  onSubmit={crearChoferRapido}
                />
                <VehiculoCrearModal
                  abierto={vehiculoCrearAbierto}
                  error={vehiculoCrearError}
                  form={vehiculoForm}
                  creando={vehiculoCreando}
                  onClose={() => setVehiculoCrearAbierto(false)}
                  onChange={handleVehiculoFormChange}
                  onSubmit={crearVehiculoRapido}
                />
                <MovimientoPlantillasModal
                  visible={plantillaListadoAbierto}
                  contextoDescripcion={plantillaListadoDescripcion}
                  plantillas={plantillasDisponibles}
                  cargando={plantillaCargando}
                  error={plantillaError}
                  mensaje={plantillaMensaje}
                  onBuscar={manejarActualizarPlantillas}
                  onCerrar={cerrarPlantillaListado}
                  onAbrirCrear={abrirCrearPlantilla}
                  onSeleccionar={aplicarPlantilla}
                  onAccionPendiente={(accion) =>
                    setPlantillaMensaje(`La acción "${accion}" estará disponible próximamente.`)
                  }
                />
                <MovimientoPlantillasCrearModal
                  visible={plantillaCrearModalAbierto}
                  requiereTalonario={Boolean(config.requiereTalonario)}
                  guardando={plantillaGuardando}
                  error={plantillaCrearError}
                  datos={plantillaCrearDatos}
                  plantillaDetalles={plantillaDetalles}
                  contextoForm={plantillaContextoForm}
                  catalogos={catalogos}
                  plantillaCatalogos={plantillaCatalogos}
                  empresaId={empresaId}
                  contextoDescripcion={plantillaCrearDescripcion}
                  onChange={handlePlantillaCrearDatosChange}
                  onContextChange={handlePlantillaContextChange}
                  onPlantillaDetalleChange={handlePlantillaDetalleChange}
                  onPlantillaDetalleAdd={agregarLineaPlantillaDetalle}
                  onPlantillaDetalleRemove={eliminarLineaPlantillaDetalle}
                  onPlantillaDetalleMove={moverLineaPlantillaDetalle}
                  onClose={cerrarPlantillaCrear}
                  onGuardar={guardarPlantilla}
                />
              </TabPane>
              <TabPane tabId="listado">
                <MovimientoListado
                  config={config}
                  busqueda={busqueda}
                  movimientos={movimientosFiltrados}
                  filtrosVisibles={filtrosVisibles}
                  onBusquedaChange={setBusqueda}
                  onLimpiarBusqueda={() => setBusqueda("")}
                  onAbrirFiltros={() => setFiltrosVisibles(true)}
                  onCerrarFiltros={() => setFiltrosVisibles(false)}
                />
              </TabPane>
            </TabContent>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default MovimientoBase;
